/**
 * Тексты главной страницы без входа (ГЛ-01) на трёх языках (НФ-16): русский, казахский, английский.
 * Остальной портал пока на русском; словарь интерфейса — отдельной задачей.
 */
import { plural } from '../../lib/format';

export type Lang = 'ru' | 'kk' | 'en';
export const LANGS: Array<{ code: Lang; title: string; short: string }> = [
  { code: 'ru', title: 'Русский', short: 'Рус' },
  { code: 'kk', title: 'Қазақша', short: 'Қаз' },
  { code: 'en', title: 'English', short: 'Eng' },
];

export interface Card {
  title: string;
  text: string;
}
export interface Shot extends Card {
  /** Имя файла в /landing/*.webp */
  image: string;
}

export interface Dictionary {
  nav: { aria: string; sections: string; how: string; plans: string; faq: string; contacts: string };
  actions: { login: string; demo: string; request: string; toPortal: string };
  hero: { title: string; slogan: string; text: string; facts: string[]; shotCaption: string };
  forWhom: { title: string; items: Card[] };
  sections: { title: string; text: string; items: Shot[] };
  how: { title: string; items: Card[] };
  plans: {
    title: string;
    text: string;
    byAgreement: string;
    perMonth: string;
    perYear: string;
    users: (n: number) => string;
    usersUnlimited: string;
    projects: (n: number) => string;
    /** Объём файлов в мегабайтах: до 1 ГБ показывается в МБ */
    files: (mb: number) => string;
    modules: string;
    /** Тариф со всеми разделами портала */
    allModules: string;
    /** Тариф без дополнительных разделов — только основа портала */
    coreOnly: string;
    /** Число разделов — для языков, где названия разделов не переведены */
    modulesCount: (n: number) => string;
    empty: string;
  };
  faq: { title: string; items: Array<{ q: string; a: string }> };
  contacts: { title: string; text: string; noContact: string; demoHint: string };
  footer: { line: string; made: string };
  signup: {
    nav: string;
    title: string;
    text: string;
    company: string;
    name: string;
    email: string;
    country: string;
    accept: string;
    offer: string;
    submit: string;
    sending: string;
    sentTitle: string;
    sentText: string;
    docsLoading: string;
    docsMissing: string;
    close: string;
    orRequest: string;
  };
  cookie: { text: string; accept: string; necessary: string; aria: string };
  lang: string;
}

const ru: Dictionary = {
  nav: {
    aria: 'Навигация по странице',
    sections: 'Разделы',
    how: 'Как устроено',
    plans: 'Тарифы',
    faq: 'Вопросы',
    contacts: 'Контакты',
  },
  actions: {
    login: 'Войти',
    demo: 'Попробовать демо',
    request: 'Запросить доступ',
    toPortal: 'В портал',
  },
  hero: {
    title: 'Портал управления строительными проектами',
    slogan: 'Проект держит курс',
    text: 'Смета, график, закупки, договоры, финансы, ПСД, надзор и склад — в одной системе, где маржа объекта считается от сметы до факта. Для EPC-подрядчиков и строительных компаний Казахстана.',
    facts: [
      'Одна база для всех разделов: заявка на закуп видна в договоре, оплате и бюджете',
      'Роли и уровень доступа к деньгам у каждого сотрудника',
      'С телефона на площадке и с компьютера в офисе',
    ],
    shotCaption: 'Портфель проектов: бюджет, маржа, оплаты, сроки и «Требует внимания» на одном экране',
  },
  forWhom: {
    title: 'Для кого',
    items: [
      {
        title: 'Руководитель компании',
        text: 'Портфель проектов, маржа, сроки и платежи на одном экране; утверждение бюджета и закупок сверх лимита.',
      },
      {
        title: 'Руководитель проекта',
        text: 'Бюджет, график, закупки и договоры проекта; «Ждёт меня» со всем, что требует решения.',
      },
      {
        title: 'Снабжение',
        text: 'Заявки, КП поставщиков, сравнение со сметой, договоры поставки, подбор со склада перед закупкой.',
      },
      {
        title: 'Сметчик',
        text: 'Импорт смет АВС и KENML, нормативная база, себестоимость разделов и варианты исполнения.',
      },
      {
        title: 'Прораб и площадка',
        text: 'График работ, ПСД и замечания надзора, выдача со склада, поручения — с телефона, без сумм.',
      },
      {
        title: 'Подрядчики и заказчик',
        text: 'Кабинет внешнего участника: свои предписания, ПСД по своему договору, давальческие материалы.',
      },
    ],
  },
  sections: {
    title: 'Разделы портала',
    text: 'Снимки сделаны на демо-компании с вымышленными данными.',
    items: [
      {
        image: 'project',
        title: 'Портфель и проекты',
        text: 'Компания → направление → проект → объект; бюджет-график, вехи, оплаты и «Требует внимания».',
      },
      {
        image: 'estimates',
        title: 'Сметы и нормативная база',
        text: 'Импорт из АВС-KZ и KENML, составление и проверка смет, выпуски нормативной базы, себестоимость разделов и варианты исполнения.',
      },
      {
        image: 'schedule',
        title: 'График и ресурсы',
        text: 'Критический путь, диаграмма Ганта, базовые планы, сценарии «что если», недельный план бригад, загрузка людей и техники.',
      },
      {
        image: 'procurement',
        title: 'Закупки и договоры',
        text: 'Заявки, КП и сравнение со сметой, договор из КП, согласование по подразделениям, графики платежей и поставок.',
      },
      {
        image: 'psd',
        title: 'ПСД, надзор и изменения',
        text: 'Комплекты и ревизии чертежей, просмотр DXF и IFC, замечания и предписания, освидетельствование скрытых работ, запросы на изменение.',
      },
      {
        image: 'warehouse',
        title: 'Склад',
        text: 'Поступление, выдача, перемещение, инвентаризация с QR, инструмент и СИЗ, давальческие у подрядчика, обмен с 1С.',
      },
      {
        image: 'finance',
        title: 'Финансы',
        text: 'Бюджет и маржа, финансовый план, платёжный календарь, заявки на оплату с комплектом документов, лимиты платежей.',
      },
      {
        image: 'inbox',
        title: '«Ждёт меня» и Pilot',
        text: 'Все дела из всех разделов одним списком, утренняя сводка на почту и в Telegram, ИИ-помощник Pilot по данным портала.',
      },
    ],
  },
  how: {
    title: 'Как устроено',
    items: [
      {
        title: 'Одна база — связи, а не копии',
        text: 'Запись заводится один раз и видна во всех разделах: поступление на склад закрывает поставку по договору, оплата — плановый платёж.',
      },
      {
        title: 'Права и деньги',
        text: 'Роли по компании, юрлицу и проекту; уровень доступа к деньгам у каждого человека — суммы скрыты от тех, кому они не нужны.',
      },
      {
        title: 'История всего',
        text: 'Каждое изменение записано: кто, что и когда, «было → стало».',
      },
      {
        title: 'Ваши данные — только ваши',
        text: 'Каждая компания изолирована на уровне базы данных. Подписка на портале или установка на ваших серверах по ключу.',
      },
      {
        title: 'Телефон на площадке',
        text: 'Кнопки под палец, без прокрутки вбок, на каждой странице «назад» и «где вы находитесь».',
      },
      {
        title: 'Стандарты Казахстана',
        text: 'Сметные нормы РК и выпуски ЭСН, перечни АГСК, формы по приказам; документы и тексты на русском.',
      },
    ],
  },
  plans: {
    title: 'Тарифы',
    text: 'Подписка на портале помесячно или на год, либо установка на серверах компании по ключу. Цены без НДС.',
    byAgreement: 'цена по договорённости',
    perMonth: 'в месяц',
    perYear: 'в год',
    users: (n) => `до ${plural(n, 'сотрудника', 'сотрудников', 'сотрудников')}`,
    usersUnlimited: 'сотрудники без ограничений',
    projects: (n) => `до ${plural(n, 'проекта', 'проектов', 'проектов')}`,
    files: (mb) => (mb < 1024 ? `файлы до ${mb} МБ` : `файлы до ${Math.round(mb / 1024)} ГБ`),
    modules: 'Разделы',
    allModules: 'все разделы портала',
    coreOnly: 'основа портала: портфель, «Ждёт меня», администрирование',
    modulesCount: (n) => plural(n, 'раздел', 'раздела', 'разделов'),
    empty: 'Тарифы уточняются — напишите нам.',
  },
  faq: {
    title: 'Частые вопросы',
    items: [
      {
        q: 'Как попробовать портал?',
        a: 'Нажмите «Попробовать демо» — откроется вымышленная компания с данными во всех разделах, роль можно сменить прямо в шапке. Каждую ночь данные демо возвращаются к исходным.',
      },
      {
        q: 'Как подключить свою компанию?',
        a: 'Оставьте заявку — владелец портала создаст компанию с пробным периодом и пришлёт приглашение администратору на почту. Дальше сотрудников добавляет сам администратор.',
      },
      {
        q: 'Где хранятся данные?',
        a: 'По подписке — в облаке портала, каждая компания изолирована на уровне базы данных. При установке у заказчика — на ваших серверах, данные к нам не передаются.',
      },
      {
        q: 'Можно ли загрузить наши сметы и графики?',
        a: 'Да: сметы из АВС-KZ и KENML, бюджет-график из Excel, графики из MS Project (XML), Primavera XER и P6 XML.',
      },
      {
        q: 'Работает ли портал с 1С?',
        a: 'Склад выгружает документы с кодами 1С в Excel и CSV и сверяет поступления; сверка оплат с 1С — следующий этап.',
      },
      {
        q: 'Что видит подрядчик?',
        a: 'Только своё: предписания, ПСД по своему договору, отчёты о давальческих материалах — без сумм.',
      },
      {
        q: 'Как устроены права?',
        a: 'Роли на уровне компании, юрлица и проекта и отдельно уровень доступа к деньгам; разделы вне тарифа скрыты из меню.',
      },
    ],
  },
  contacts: {
    title: 'Контакты',
    text: 'Напишите владельцу портала — ответим и покажем портал на ваших задачах.',
    noContact: 'Оставьте заявку — свяжемся с вами.',
    demoHint: 'Или посмотрите демо прямо сейчас — без регистрации.',
  },
  footer: {
    line: 'EPC Pilot AI — портал управления строительными проектами.',
    made: 'Снимки экранов — демо-компания с вымышленными данными.',
  },
  signup: {
    nav: 'Регистрация',
    title: 'Попробовать портал для своей компании',
    text: 'Создайте компанию сами: подтвердите почту по письму — и начнётся пробный период без оплаты.',
    company: 'Название компании',
    name: 'Ваше имя',
    email: 'Рабочая почта',
    country: 'Страна',
    accept: 'Я принимаю',
    offer: 'условия оферты и политику данных',
    submit: 'Зарегистрировать компанию',
    sending: 'Отправляем',
    sentTitle: 'Проверьте почту',
    sentText:
      'Если адрес верный, мы отправили письмо со ссылкой для подтверждения. Она действует несколько часов; загляните и в папку «Спам».',
    docsLoading: 'Загружаем документы',
    docsMissing: 'Для выбранной страны документы пока не опубликованы — оставьте заявку на доступ.',
    close: 'Закрыть',
    orRequest: 'Нужна консультация? Оставьте заявку на доступ.',
  },
  cookie: {
    text: 'Мы используем только необходимые файлы cookie и память браузера: вход в портал и выбранный язык. Рекламных и аналитических cookie нет.',
    accept: 'Понятно',
    necessary: 'Только необходимые',
    aria: 'Сообщение о файлах cookie',
  },
  lang: 'Язык',
};

const kk: Dictionary = {
  nav: {
    aria: 'Бет бойынша навигация',
    sections: 'Бөлімдер',
    how: 'Қалай жұмыс істейді',
    plans: 'Тарифтер',
    faq: 'Сұрақтар',
    contacts: 'Байланыс',
  },
  actions: {
    login: 'Кіру',
    demo: 'Демоны көру',
    request: 'Қолжетімділік сұрау',
    toPortal: 'Порталға',
  },
  hero: {
    title: 'Құрылыс жобаларын басқару порталы',
    slogan: 'Жоба бағытын сақтайды',
    text: 'Смета, кесте, сатып алу, шарттар, қаржы, ЖСҚ, қадағалау және қойма — бір жүйеде, онда нысанның маржасы сметадан нақты шығынға дейін есептеледі. Қазақстанның EPC мердігерлері мен құрылыс компаниялары үшін.',
    facts: [
      'Барлық бөлімге бір база: сатып алу өтінімі шартта, төлемде және бюджетте көрінеді',
      'Әр қызметкерде рөлдер және ақшаға қолжетімділік деңгейі',
      'Алаңда телефоннан, кеңседе компьютерден',
    ],
    shotCaption: 'Жобалар портфелі: бюджет, маржа, төлемдер, мерзімдер және «Назар аудару керек» бір экранда',
  },
  forWhom: {
    title: 'Кімге арналған',
    items: [
      {
        title: 'Компания басшысы',
        text: 'Жобалар портфелі, маржа, мерзімдер мен төлемдер бір экранда; бюджетті және лимиттен тыс сатып алуды бекіту.',
      },
      {
        title: 'Жоба жетекшісі',
        text: 'Жобаның бюджеті, кестесі, сатып алуы мен шарттары; шешім күтетін істердің бәрі «Мені күтеді» тізімінде.',
      },
      {
        title: 'Жабдықтау',
        text: 'Өтінімдер, жеткізушілердің коммерциялық ұсыныстары, сметамен салыстыру, жеткізу шарттары, сатып алудан бұрын қоймадан іріктеу.',
      },
      {
        title: 'Сметашы',
        text: 'АВС және KENML сметаларын импорттау, нормативтік база, бөлімдердің өзіндік құны мен орындау нұсқалары.',
      },
      {
        title: 'Прораб және алаң',
        text: 'Жұмыс кестесі, ЖСҚ және қадағалау ескертулері, қоймадан беру, тапсырмалар — телефоннан, сомасыз.',
      },
      {
        title: 'Мердігерлер мен тапсырыс беруші',
        text: 'Сыртқы қатысушы кабинеті: өз нұсқамалары, өз шарты бойынша ЖСҚ, тапсырыс беруші материалдары.',
      },
    ],
  },
  sections: {
    title: 'Портал бөлімдері',
    text: 'Суреттер ойдан шығарылған деректері бар демо-компанияда түсірілген.',
    items: [
      {
        image: 'project',
        title: 'Портфель және жобалар',
        text: 'Компания → бағыт → жоба → нысан; бюджет-кесте, кезеңдер, төлемдер және «Назар аудару керек».',
      },
      {
        image: 'estimates',
        title: 'Сметалар және нормативтік база',
        text: 'АВС-KZ және KENML-ден импорт, смета құру мен тексеру, нормативтік база шығарылымдары, бөлімдердің өзіндік құны және орындау нұсқалары.',
      },
      {
        image: 'schedule',
        title: 'Кесте және ресурстар',
        text: 'Сыни жол, Гант диаграммасы, базалық жоспарлар, «егер» сценарийлері, бригадалардың апталық жоспары, адамдар мен техника жүктемесі.',
      },
      {
        image: 'procurement',
        title: 'Сатып алу және шарттар',
        text: 'Өтінімдер, коммерциялық ұсыныстар және сметамен салыстыру, ұсыныстан шарт, бөлімшелер бойынша келісу, төлем мен жеткізу кестелері.',
      },
      {
        image: 'psd',
        title: 'ЖСҚ, қадағалау және өзгерістер',
        text: 'Сызба жинақтары мен ревизиялары, DXF және IFC қарау, ескертулер мен нұсқамалар, жасырын жұмыстарды куәландыру, өзгеріс сұраулары.',
      },
      {
        image: 'warehouse',
        title: 'Қойма',
        text: 'Кіріс, беру, орын ауыстыру, QR арқылы түгендеу, құрал-сайман мен ЖҚҚ, мердігердегі тапсырыс беруші материалдары, 1С-пен алмасу.',
      },
      {
        image: 'finance',
        title: 'Қаржы',
        text: 'Бюджет пен маржа, қаржы жоспары, төлем күнтізбесі, құжаттар жинағымен төлем өтінімдері, төлем лимиттері.',
      },
      {
        image: 'inbox',
        title: '«Мені күтеді» және Pilot',
        text: 'Барлық бөлімдердің істері бір тізімде, поштаға және Telegram-ға таңғы жиынтық, портал деректері бойынша Pilot ЖИ-көмекшісі.',
      },
    ],
  },
  how: {
    title: 'Қалай жұмыс істейді',
    items: [
      {
        title: 'Бір база — көшірме емес, байланыс',
        text: 'Жазба бір рет енгізіледі және барлық бөлімде көрінеді: қоймаға кіріс шарт бойынша жеткізуді жабады, төлем — жоспарлы төлемді.',
      },
      {
        title: 'Құқықтар мен ақша',
        text: 'Компания, заңды тұлға және жоба бойынша рөлдер; әр адамда ақшаға қолжетімділік деңгейі — сомалар оларды қажет етпейтіндерден жасырылады.',
      },
      {
        title: 'Бәрінің тарихы',
        text: 'Әр өзгеріс жазылады: кім, не және қашан, «бұрын → қазір».',
      },
      {
        title: 'Деректеріңіз — тек сіздікі',
        text: 'Әр компания дерекқор деңгейінде оқшауланған. Порталға жазылым немесе кілт бойынша өз серверлеріңізге орнату.',
      },
      {
        title: 'Алаңдағы телефон',
        text: 'Саусаққа ыңғайлы түймелер, бүйірге айналдырусыз, әр бетте «артқа» және «сіз қайдасыз».',
      },
      {
        title: 'Қазақстан стандарттары',
        text: 'ҚР сметалық нормалары және ЭСН шығарылымдары, АГСК тізбелері, бұйрықтар бойынша нысандар; құжаттар мен мәтіндер орыс тілінде.',
      },
    ],
  },
  plans: {
    title: 'Тарифтер',
    text: 'Порталға ай сайын немесе жылдық жазылым, не кілт бойынша компания серверлеріне орнату. Бағалар ҚҚС-сыз.',
    byAgreement: 'бағасы келісім бойынша',
    perMonth: 'айына',
    perYear: 'жылына',
    users: (n) => `${n} қызметкерге дейін`,
    usersUnlimited: 'қызметкерлер саны шектеусіз',
    projects: (n) => `${n} жобаға дейін`,
    files: (mb) => (mb < 1024 ? `файлдар ${mb} МБ-қа дейін` : `файлдар ${Math.round(mb / 1024)} ГБ-қа дейін`),
    modules: 'Бөлімдер',
    allModules: 'порталдың барлық бөлімдері',
    coreOnly: 'порталдың негізі: портфель, «Мені күтеді», әкімшілендіру',
    modulesCount: (n) => `${n} бөлім (атаулары орыс тілінде)`,
    empty: 'Тарифтер нақтылануда — бізге жазыңыз.',
  },
  faq: {
    title: 'Жиі қойылатын сұрақтар',
    items: [
      {
        q: 'Порталды қалай көруге болады?',
        a: '«Демоны көру» түймесін басыңыз — барлық бөлімде деректері бар ойдан шығарылған компания ашылады, рөлді жоғарғы жолақта ауыстыруға болады. Демо деректері әр түнде бастапқы күйіне оралады.',
      },
      {
        q: 'Өз компаниямызды қалай қосамыз?',
        a: 'Өтінім қалдырыңыз — портал иесі сынақ мерзімімен компания құрып, әкімшінің поштасына шақыру жібереді. Әрі қарай қызметкерлерді әкімшінің өзі қосады.',
      },
      {
        q: 'Деректер қайда сақталады?',
        a: 'Жазылым бойынша — портал бұлтында, әр компания дерекқор деңгейінде оқшауланған. Тапсырыс берушіде орнатылғанда — сіздің серверлеріңізде, деректер бізге берілмейді.',
      },
      {
        q: 'Өз сметаларымыз бен кестелерімізді жүктей аламыз ба?',
        a: 'Иә: АВС-KZ және KENML сметалары, Excel-ден бюджет-кесте, MS Project (XML), Primavera XER және P6 XML кестелері.',
      },
      {
        q: 'Портал 1С-пен жұмыс істей ме?',
        a: 'Қойма құжаттарды 1С кодтарымен Excel және CSV-ге шығарады және кірістерді салыстырады; төлемдерді 1С-пен салыстыру — келесі кезең.',
      },
      {
        q: 'Мердігер нені көреді?',
        a: 'Тек өзінікін: нұсқамалар, өз шарты бойынша ЖСҚ, тапсырыс беруші материалдары туралы есептер — сомасыз.',
      },
      {
        q: 'Құқықтар қалай құрылған?',
        a: 'Компания, заңды тұлға және жоба деңгейіндегі рөлдер және бөлек ақшаға қолжетімділік деңгейі; тарифке кірмейтін бөлімдер мәзірде көрінбейді.',
      },
    ],
  },
  contacts: {
    title: 'Байланыс',
    text: 'Портал иесіне жазыңыз — жауап береміз және порталды сіздің міндеттеріңізде көрсетеміз.',
    noContact: 'Өтінім қалдырыңыз — сізбен байланысамыз.',
    demoHint: 'Немесе демоны дәл қазір көріңіз — тіркеусіз.',
  },
  footer: {
    line: 'EPC Pilot AI — құрылыс жобаларын басқару порталы.',
    made: 'Экран суреттері — ойдан шығарылған деректері бар демо-компания.',
  },
  signup: {
    nav: 'Тіркелу',
    title: 'Порталды өз компанияңызға сынап көріңіз',
    text: 'Компанияны өзіңіз құрыңыз: хаттағы сілтеме арқылы поштаны растаңыз — төлемсіз сынақ мерзімі басталады.',
    company: 'Компания атауы',
    name: 'Атыңыз',
    email: 'Жұмыс поштасы',
    country: 'Ел',
    accept: 'Мен қабылдаймын:',
    offer: 'оферта шарттарын және деректер саясатын',
    submit: 'Компанияны тіркеу',
    sending: 'Жіберілуде',
    sentTitle: 'Поштаңызды тексеріңіз',
    sentText:
      'Мекенжай дұрыс болса, растау сілтемесі бар хат жібердік. Сілтеме бірнеше сағат жарамды; «Спам» қалтасын да қараңыз.',
    docsLoading: 'Құжаттар жүктелуде',
    docsMissing: 'Таңдалған ел үшін құжаттар әлі жарияланбаған — қолжетімділікке өтінім қалдырыңыз.',
    close: 'Жабу',
    orRequest: 'Кеңес керек пе? Қолжетімділікке өтінім қалдырыңыз.',
  },
  cookie: {
    text: 'Біз тек қажетті cookie файлдары мен браузер жадын қолданамыз: порталға кіру және таңдалған тіл. Жарнамалық және аналитикалық cookie жоқ.',
    accept: 'Түсінікті',
    necessary: 'Тек қажеттілері',
    aria: 'Cookie файлдары туралы хабар',
  },
  lang: 'Тіл',
};

const en: Dictionary = {
  nav: {
    aria: 'Page navigation',
    sections: 'Modules',
    how: 'How it works',
    plans: 'Plans',
    faq: 'FAQ',
    contacts: 'Contact',
  },
  actions: {
    login: 'Sign in',
    demo: 'Try the demo',
    request: 'Request access',
    toPortal: 'Open the portal',
  },
  hero: {
    title: 'Construction project management portal',
    slogan: 'Keep every project on course',
    text: 'Estimates, schedule, procurement, contracts, finance, design documents, supervision and warehouse in one system, where the margin of each site is tracked from the estimate to actuals. Built for EPC contractors and construction companies in Kazakhstan.',
    facts: [
      'One database for every module: a purchase request is visible in the contract, the payment and the budget',
      'Roles and a money-access level for every employee',
      'Works on a phone at the site and on a computer in the office',
    ],
    shotCaption: 'Project portfolio: budget, margin, payments, deadlines and "Needs attention" on one screen',
  },
  forWhom: {
    title: 'Who it is for',
    items: [
      {
        title: 'Company director',
        text: 'Project portfolio, margin, deadlines and payments on one screen; approval of budgets and over-limit purchases.',
      },
      {
        title: 'Project manager',
        text: 'Project budget, schedule, procurement and contracts; an inbox with everything that needs a decision.',
      },
      {
        title: 'Procurement',
        text: 'Purchase requests, supplier quotes, comparison with the estimate, supply contracts, sourcing from stock before buying.',
      },
      {
        title: 'Estimator',
        text: 'Import of ABC and KENML estimates, the normative base, section cost and execution options.',
      },
      {
        title: 'Site manager and crews',
        text: 'Work schedule, drawings and supervision remarks, warehouse issue, tasks — from a phone, without prices.',
      },
      {
        title: 'Contractors and the client',
        text: 'External participant workspace: own instructions, drawings under own contract, client-supplied materials.',
      },
    ],
  },
  sections: {
    title: 'Portal modules',
    text: 'Screenshots are taken on the demo company with fictional data.',
    items: [
      {
        image: 'project',
        title: 'Portfolio and projects',
        text: 'Company → business line → project → site; budget-schedule, milestones, payments and "Needs attention".',
      },
      {
        image: 'estimates',
        title: 'Estimates and normative base',
        text: 'Import from ABC-KZ and KENML, drafting and checking estimates, normative base releases, section cost and execution options.',
      },
      {
        image: 'schedule',
        title: 'Schedule and resources',
        text: 'Critical path, Gantt chart, baselines, what-if scenarios, weekly crew plan, workload of people and machinery.',
      },
      {
        image: 'procurement',
        title: 'Procurement and contracts',
        text: 'Requests, quotes compared with the estimate, a contract from the winning quote, departmental approval, payment and delivery schedules.',
      },
      {
        image: 'psd',
        title: 'Design documents, supervision and changes',
        text: 'Drawing sets and revisions, DXF and IFC viewers, remarks and instructions, inspection of hidden works, change requests.',
      },
      {
        image: 'warehouse',
        title: 'Warehouse',
        text: 'Receipts, issue, transfers, stocktaking with QR, tools and PPE, client-supplied materials at contractors, 1C exchange.',
      },
      {
        image: 'finance',
        title: 'Finance',
        text: 'Budget and margin, financial plan, payment calendar, payment requests with a document set, payment limits.',
      },
      {
        image: 'inbox',
        title: 'Inbox and Pilot',
        text: 'Everything waiting for you from every module in one list, a morning digest by email and Telegram, the Pilot AI assistant working on portal data.',
      },
    ],
  },
  how: {
    title: 'How it works',
    items: [
      {
        title: 'One database — links, not copies',
        text: 'A record is entered once and is visible in every module: a warehouse receipt closes the contract delivery, a payment closes the planned instalment.',
      },
      {
        title: 'Rights and money',
        text: 'Roles per company, legal entity and project; a money-access level for every person — amounts are hidden from those who do not need them.',
      },
      {
        title: 'History of everything',
        text: 'Every change is recorded: who, what and when, "was → became".',
      },
      {
        title: 'Your data stays yours',
        text: 'Every company is isolated at the database level. Subscription on the portal, or installation on your own servers with a licence key.',
      },
      {
        title: 'Phone at the site',
        text: 'Finger-sized buttons, no sideways scrolling, "back" and "where you are" on every page.',
      },
      {
        title: 'Kazakhstan standards',
        text: 'Kazakhstan estimate norms and ESN releases, AGSK registers, forms per ministry orders; documents and texts in Russian.',
      },
    ],
  },
  plans: {
    title: 'Plans',
    text: 'Monthly or yearly subscription on the portal, or installation on the company servers with a licence key. Prices exclude VAT.',
    byAgreement: 'price on request',
    perMonth: 'per month',
    perYear: 'per year',
    users: (n) => `up to ${n} employee${n === 1 ? '' : 's'}`,
    usersUnlimited: 'unlimited employees',
    projects: (n) => `up to ${n} project${n === 1 ? '' : 's'}`,
    files: (mb) => (mb < 1024 ? `files up to ${mb} MB` : `files up to ${Math.round(mb / 1024)} GB`),
    modules: 'Modules',
    allModules: 'all portal modules',
    coreOnly: 'portal core: portfolio, “Waiting for me”, administration',
    modulesCount: (n) => `${n} module${n === 1 ? '' : 's'} (names in Russian)`,
    empty: 'Plans are being finalised — contact us.',
  },
  faq: {
    title: 'Frequently asked questions',
    items: [
      {
        q: 'How can I try the portal?',
        a: 'Press "Try the demo" — a fictional company with data in every module opens, and the role can be switched in the header. Demo data is reset every night.',
      },
      {
        q: 'How do I connect my company?',
        a: 'Leave a request — the portal owner creates the company with a trial period and sends an invitation to the administrator by email. The administrator then adds the employees.',
      },
      {
        q: 'Where is the data stored?',
        a: 'With a subscription — in the portal cloud, each company isolated at the database level. With an on-premise installation — on your servers; no data is sent to us.',
      },
      {
        q: 'Can we load our estimates and schedules?',
        a: 'Yes: estimates from ABC-KZ and KENML, a budget-schedule from Excel, schedules from MS Project (XML), Primavera XER and P6 XML.',
      },
      {
        q: 'Does the portal work with 1C?',
        a: 'The warehouse exports documents with 1C codes to Excel and CSV and reconciles receipts; payment reconciliation with 1C is the next stage.',
      },
      {
        q: 'What does a contractor see?',
        a: 'Only their own: instructions, drawings under their contract, reports on client-supplied materials — without amounts.',
      },
      {
        q: 'How are rights organised?',
        a: 'Roles at the company, legal entity and project level plus a separate money-access level; modules outside the plan are hidden from the menu.',
      },
    ],
  },
  contacts: {
    title: 'Contact',
    text: 'Write to the portal owner — we will reply and show the portal on your own tasks.',
    noContact: 'Leave a request — we will get back to you.',
    demoHint: 'Or look at the demo right now — no registration needed.',
  },
  footer: {
    line: 'EPC Pilot AI — construction project management portal.',
    made: 'Screenshots show the demo company with fictional data.',
  },
  signup: {
    nav: 'Sign up',
    title: 'Try the portal for your company',
    text: 'Create the company yourself: confirm your email from the message we send and the free trial starts.',
    company: 'Company name',
    name: 'Your name',
    email: 'Work email',
    country: 'Country',
    accept: 'I accept the',
    offer: 'terms of the offer and the privacy policy',
    submit: 'Register company',
    sending: 'Sending',
    sentTitle: 'Check your email',
    sentText:
      'If the address is correct, we have sent a message with a confirmation link. It is valid for a few hours; check your spam folder too.',
    docsLoading: 'Loading documents',
    docsMissing: 'Documents for the selected country are not published yet. Please request access instead.',
    close: 'Close',
    orRequest: 'Need a consultation? Request access.',
  },
  cookie: {
    text: 'We only use necessary cookies and browser storage: portal sign-in and your chosen language. There are no advertising or analytics cookies.',
    accept: 'Got it',
    necessary: 'Necessary only',
    aria: 'Cookie notice',
  },
  lang: 'Language',
};

export const DICT: Record<Lang, Dictionary> = { ru, kk, en };

const STORAGE_KEY = 'epc:landing:lang';

/** Язык по умолчанию: сохранённый выбор, иначе язык браузера, иначе русский */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ru' || saved === 'kk' || saved === 'en') return saved;
  } catch {
    /* хранилище недоступно — язык браузера */
  }
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'ru').toLowerCase();
  if (nav.startsWith('kk') || nav.startsWith('kz')) return 'kk';
  if (nav.startsWith('en')) return 'en';
  return 'ru';
}

const COOKIE_KEY = 'epc:cookie-notice';

/** Сообщение о cookie уже закрыто? Хранилище недоступно — показываем снова, это безопасно */
export function cookieNoticeSeen(): boolean {
  try {
    return localStorage.getItem(COOKIE_KEY) !== null;
  } catch {
    return false;
  }
}

export function rememberCookieNotice(choice: 'all' | 'necessary'): void {
  try {
    localStorage.setItem(COOKIE_KEY, choice);
  } catch {
    /* не страшно */
  }
}

export function rememberLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* не страшно */
  }
}
